// =====================================================
// AUTHENTICATION CONTEXT
// Centralized authentication state and token management for LiquidStack
// =====================================================

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useLogin, useLogout, useMe, type User, type AuthResponse } from '@/lib/graphql/hooks';
import { ApolloError } from '@apollo/client';

// =====================================================
// TYPES
// =====================================================

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  user: User | null;
  error: string | null;
}

export interface AuthContextValue extends AuthState {
  // Authentication actions
  login: (email: string, password: string) => Promise<AuthResponse>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;

  // State helpers
  clearError: () => void;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  isAdmin: () => boolean;
}

interface AuthProviderProps {
  children: React.ReactNode;
  /** Auto-refresh token interval in milliseconds (default: 5 minutes before expiry) */
  tokenRefreshInterval?: number;
  /** Enable debug logging */
  debug?: boolean;
}

// =====================================================
// CONTEXT
// =====================================================

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// =====================================================
// STORAGE HELPERS
// =====================================================

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'liquidstack_access_token',
  REFRESH_TOKEN: 'liquidstack_refresh_token',
  TOKEN_EXPIRES_AT: 'liquidstack_token_expires_at',
  USER_DATA: 'liquidstack_user_data',
} as const;

export class TokenStorage {
  static setAccessToken(token: string, expiresIn: number) {
    const expiresAt = Date.now() + expiresIn * 1000;
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
    localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRES_AT, expiresAt.toString());
  }

  static getAccessToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  static setRefreshToken(token: string) {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
  }

  static getRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  static getTokenExpiresAt(): number | null {
    const expiresAt = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
    return expiresAt ? parseInt(expiresAt, 10) : null;
  }

  static isTokenExpired(): boolean {
    const expiresAt = this.getTokenExpiresAt();
    if (!expiresAt) return true;
    // Add 30 second buffer to refresh before actual expiry
    return Date.now() >= expiresAt - 30000;
  }

  static getTimeUntilExpiry(): number | null {
    const expiresAt = this.getTokenExpiresAt();
    if (!expiresAt) return null;
    return Math.max(0, expiresAt - Date.now());
  }

  static setUserData(user: User) {
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
  }

  static getUserData(): User | null {
    const data = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    if (!data) return null;
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }

  static clear() {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  }

  // For backward compatibility with existing code
  static setLegacyToken(token: string) {
    localStorage.setItem('auth_token', token);
  }

  static clearLegacyToken() {
    localStorage.removeItem('auth_token');
  }
}

// =====================================================
// PROVIDER
// =====================================================

export function AuthProvider({
  children,
  tokenRefreshInterval = 300000, // 5 minutes default
  debug = false,
}: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    isInitialized: false,
    user: null,
    error: null,
  });

  const loginMutation = useLogin({
    onCompleted: (data) => {
      logDebug('Login completed successfully', data);
    },
    onError: (error) => {
      logDebug('Login mutation error:', error.message);
    },
  });

  const logoutMutation = useLogout({
    onCompleted: () => {
      logDebug('Logout mutation completed');
    },
  });

  // Prevent infinite loop in useEffect
  const isInitializedRef = useRef(false);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const logDebug = (...args: unknown[]) => {
    if (debug) {
      console.log('[AuthContext]', ...args);
    }
  };

  // =====================================================
  // AUTHENTICATION ACTIONS
  // =====================================================

  const login = useCallback(async (email: string, password: string): Promise<AuthResponse> => {
    logDebug('Attempting login for:', email);
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await loginMutation[0]({ variables: { email, password } });

      if (!result.data?.login) {
        throw new Error('Login response was empty');
      }

      const { user, accessToken, refreshToken, expiresIn } = result.data.login;

      // Store tokens and user data
      TokenStorage.setAccessToken(accessToken, expiresIn);
      TokenStorage.setRefreshToken(refreshToken);
      TokenStorage.setUserData(user);
      TokenStorage.setLegacyToken(accessToken); // For backward compatibility

      setState({
        isAuthenticated: true,
        isLoading: false,
        isInitialized: true,
        user,
        error: null,
      });

      // Setup token refresh timer
      scheduleTokenRefresh(expiresIn);

      logDebug('Login successful for user:', user.id);
      return result.data.login;
    } catch (error) {
      const errorMessage = error instanceof ApolloError
        ? error.message
        : 'Login failed. Please check your credentials.';

      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));

      logDebug('Login failed:', errorMessage);
      throw error;
    }
  }, [loginMutation, debug]);

  const logout = useCallback(async (): Promise<void> => {
    logDebug('Logging out user');

    try {
      // Call logout mutation if we have a token
      if (TokenStorage.getAccessToken()) {
        await logoutMutation[0]();
      }
    } catch (error) {
      logDebug('Logout mutation failed (continuing anyway):', error);
    } finally {
      // Clear all stored data regardless of mutation result
      TokenStorage.clear();
      TokenStorage.clearLegacyToken();

      // Clear refresh timer
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }

      // Reset Apollo client cache (via window event for Apollo client)
      window.dispatchEvent(new Event('auth-logout'));

      setState({
        isAuthenticated: false,
        isLoading: false,
        isInitialized: true,
        user: null,
        error: null,
      });

      logDebug('Logout complete');
    }
  }, [logoutMutation, debug]);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    const currentRefreshToken = TokenStorage.getRefreshToken();

    if (!currentRefreshToken) {
      logDebug('No refresh token available');
      await logout();
      return false;
    }

    logDebug('Attempting token refresh');

    try {
      // This would call a refresh mutation - for now, we'll log the user out
      // In production, this should call a GraphQL mutation to refresh the token
      logDebug('Token refresh not fully implemented - logging out');
      await logout();
      return false;
    } catch (error) {
      logDebug('Token refresh failed:', error);
      await logout();
      return false;
    }
  }, [logout, debug]);

  // =====================================================
  // STATE HELPERS
  // =====================================================

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  const hasPermission = useCallback((permission: string): boolean => {
    return state.user?.permissions?.includes(permission) ?? false;
  }, [state.user?.permissions]);

  const hasRole = useCallback((role: string): boolean => {
    return state.user?.role === role;
  }, [state.user?.role]);

  const isAdmin = useCallback((): boolean => {
    return hasRole('admin') || hasRole('owner');
  }, [hasRole]);

  // =====================================================
  // INITIALIZATION & TOKEN REFRESH
  // =====================================================

  const scheduleTokenRefresh = useCallback((expiresIn: number) => {
    // Clear existing timer
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // Schedule refresh for 5 minutes before expiry
    const refreshDelay = Math.max(0, expiresIn * 1000 - tokenRefreshInterval);

    logDebug('Scheduling token refresh in', Math.floor(refreshDelay / 1000), 'seconds');

    refreshTimeoutRef.current = setTimeout(() => {
      logDebug('Token refresh timer triggered');
      refreshToken().catch((error) => {
        logDebug('Scheduled token refresh failed:', error);
      });
    }, refreshDelay);
  }, [tokenRefreshInterval, refreshToken, debug]);

  const initializeAuth = useCallback(async () => {
    if (isInitializedRef.current) {
      return;
    }

    isInitializedRef.current = true;
    logDebug('Initializing authentication');

    const accessToken = TokenStorage.getAccessToken();
    const savedUser = TokenStorage.getUserData();

    if (!accessToken) {
      logDebug('No access token found - user not authenticated');
      setState({
        isAuthenticated: false,
        isLoading: false,
        isInitialized: true,
        user: null,
        error: null,
      });
      return;
    }

    // Check if token is expired
    if (TokenStorage.isTokenExpired()) {
      logDebug('Access token expired - attempting refresh');
      const refreshed = await refreshToken();
      if (!refreshed) {
        setState({
          isAuthenticated: false,
          isLoading: false,
          isInitialized: true,
          user: null,
          error: null,
        });
      }
      return;
    }

    // Use saved user data and mark as authenticated
    logDebug('Valid access token found - user authenticated');
    if (savedUser) {
      const timeUntilExpiry = TokenStorage.getTimeUntilExpiry();
      if (timeUntilExpiry !== null) {
        scheduleTokenRefresh(Math.floor(timeUntilExpiry / 1000));
      }

      setState({
        isAuthenticated: true,
        isLoading: false,
        isInitialized: true,
        user: savedUser,
        error: null,
      });
    } else {
      // We have a token but no user data - try to fetch user
      logDebug('Token found but no user data - user might need to re-login');
      setState({
        isAuthenticated: false,
        isLoading: false,
        isInitialized: true,
        user: null,
        error: null,
      });
    }
  }, [refreshToken, scheduleTokenRefresh, debug]);

  // =====================================================
  // EFFECTS
  // =====================================================

  // Initialize auth on mount
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  // Listen for storage changes (multi-tab sync)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === STORAGE_KEYS.ACCESS_TOKEN && !event.newValue) {
        // Token was removed in another tab - logout here too
        logout();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [logout]);

  // =====================================================
  // CONTEXT VALUE
  // =====================================================

  const value: AuthContextValue = useMemo(
    () => ({
      ...state,
      login,
      logout,
      refreshToken,
      clearError,
      hasPermission,
      hasRole,
      isAdmin,
    }),
    [
      state,
      login,
      logout,
      refreshToken,
      clearError,
      hasPermission,
      hasRole,
      isAdmin,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// =====================================================
// HOOKS FOR SPECIFIC AUTH FEATURES
// =====================================================

/**
 * Hook that returns whether the current user has a specific permission
 */
export function useHasPermission(permission: string): boolean {
  const { hasPermission } = useAuth();
  return hasPermission(permission);
}

/**
 * Hook that returns whether the current user has a specific role
 */
export function useHasRole(role: string): boolean {
  const { hasRole } = useAuth();
  return hasRole(role);
}

/**
 * Hook that returns whether the current user is an admin
 */
export function useIsAdmin(): boolean {
  const { isAdmin } = useAuth();
  return isAdmin();
}

/**
 * Hook that returns the current authenticated user
 */
export function useCurrentUser(): User | null {
  const { user } = useAuth();
  return user;
}

/**
 * Hook that returns authentication status flags
 */
export function useAuthStatus(): {
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
} {
  const { isAuthenticated, isLoading, isInitialized } = useAuth();
  return { isAuthenticated, isLoading, isInitialized };
}

// =====================================================
// HIGHER-ORDER COMPONENTS
// =====================================================

/**
 * HOC that requires authentication to render the component
 */
export function withAuth<P extends object>(
  Component: React.ComponentType<P>
): React.ComponentType<P> {
  return function AuthenticatedComponent(props: P) {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      );
    }

    if (!isAuthenticated) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Authentication Required</h1>
          <p className="text-gray-600 mb-4">Please log in to access this page.</p>
          <a
            href="/login"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Login
          </a>
        </div>
      );
    }

    return <Component {...props} />;
  };
}

/**
 * HOC that requires specific permissions to render the component
 */
export function withPermission<P extends object>(
  Component: React.ComponentType<P>,
  requiredPermission: string
): React.ComponentType<P> {
  return function PermissionComponent(props: P) {
    const { hasPermission, isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      );
    }

    if (!isAuthenticated || !hasPermission(requiredPermission)) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">
            You don't have permission to access this page.
          </p>
        </div>
      );
    }

    return <Component {...props} />;
  };
}

/**
 * HOC that requires admin role to render the component
 */
export function withAdmin<P extends object>(
  Component: React.ComponentType<P>
): React.ComponentType<P> {
  return withPermission(Component, 'admin');
}
